import { hydrateCreatedAssessments } from "@/lib/created-assessments"
import {
  findCourseOfferingAssessment,
  offeringAssessmentStatusBySlug,
} from "@/lib/mock/course-offering-assessments"

export interface CourseOffering {
  [key: string]: any
  id: string
  code: string
  title: string
  academicYear: string
  term: string
  cohort: string
  startDate: string
  endDate: string
  credits: number
  instructor: string
  enrollmentCount: number
  status: "Active" | "Draft" | "Archived"
  courseType: "Lecture" | "Seminar" | "Lab" | "Project"
  assessmentCounts: {
    draft: number
    scheduled: number
    live: number
    evaluating: number
    resultsPublished: number
  }
}

export interface Assessment {
  [key: string]: any
  id: string
  code: string
  title: string
  course: string
  dueDate: string
  questionCount: number
  status: "Draft" | "Scheduled" | "Live" | "Evaluating" | "Results Published"
  type?: "Exam" | "Quiz" | "Practical"
  /** Planned marks. New assessments are 100. Never above 100. */
  totalMarks?: number
}

export interface Submission {
  [key: string]: any
  id: string
  studentId: string
  studentName: string
  assessmentTitle: string
  submittedAt: string
  grade: string
  score: number
  status: "In-grading" | "Graded" | ""
}

export interface StudentRecord {
  [key: string]: any
  id: string
  studentId: string
  name: string
  email: string
  phone: string
  gpa: number
  courses: string[]
  assessments: string[]
  status: "Active" | "Inactive"
  group: string
  enrollment: string
  graduation: string
  campus: string
  cohort: string
  studentCategory: string
}

export interface FacultyRecord {
  [key: string]: any
  id: string
  facultyId: string
  name: string
  email: string
  phone: string
  status: "Active" | "Inactive"
  role: "Instructor" | "Co-Instructor" | "Teaching Assistant"
  facultyType: "Faculty" | "Staff"
  adminPosition: string
  rank: string
  lastUpdated: string
  department: string
  campus: string
  category: "Full-time" | "Part-time" | "Adjunct"
  courses: string[]
  assessmentsCount: number
}

export const MOCK_COURSES: CourseOffering[] = [
  {
    id: "c1",
    code: "NURS-210",
    title: "Clinical Sciences I",
    academicYear: "2025-2026",
    term: "Summer 2026",
    cohort: "BSN Cohort Alpha",
    startDate: "2026-06-01",
    endDate: "2026-08-31",
    credits: 4,
    instructor: "Dr. Amara Okonkwo",
    enrollmentCount: 45,
    status: "Active",
    courseType: "Lecture",
    assessmentCounts: { draft: 4, scheduled: 1, live: 1, evaluating: 2, resultsPublished: 1 }
  },
  {
    id: "c2",
    code: "NURS-320",
    title: "Pharmacology",
    academicYear: "2025-2026",
    term: "Summer 2026",
    cohort: "BSN Cohort Bravo",
    startDate: "2026-06-01",
    endDate: "2026-08-31",
    credits: 4,
    instructor: "Dr. Priya Raman",
    enrollmentCount: 38,
    status: "Active",
    courseType: "Lecture",
    assessmentCounts: { draft: 0, scheduled: 1, live: 1, evaluating: 6, resultsPublished: 0 }
  },
  {
    id: "c3",
    code: "NURS-230",
    title: "Health Assessment",
    academicYear: "2025-2026",
    term: "Spring 2026",
    cohort: "BSN Cohort Bravo",
    startDate: "2026-01-15",
    endDate: "2026-05-15",
    credits: 3,
    instructor: "Dr. Elena Vasquez",
    enrollmentCount: 29,
    status: "Active",
    courseType: "Lecture",
    assessmentCounts: { draft: 0, scheduled: 0, live: 0, evaluating: 5, resultsPublished: 0 }
  },
  {
    id: "c4",
    code: "NURS-410",
    title: "Community Health Nursing",
    academicYear: "2026-2027",
    term: "Fall 2026",
    cohort: "BSN Cohort Charlie",
    startDate: "2026-09-01",
    endDate: "2026-12-15",
    credits: 3,
    instructor: "Dr. Camille Brooks",
    enrollmentCount: 50,
    status: "Draft",
    courseType: "Lecture",
    assessmentCounts: { draft: 0, scheduled: 4, live: 0, evaluating: 0, resultsPublished: 0 }
  },
  {
    id: "c5",
    code: "NURS-330",
    title: "Adult Health Nursing I",
    academicYear: "2025-2026",
    term: "Summer 2026",
    cohort: "BSN Cohort Alpha",
    startDate: "2026-06-01",
    endDate: "2026-08-31",
    credits: 4,
    instructor: "Dr. Jordan Hale",
    enrollmentCount: 18,
    status: "Active",
    courseType: "Lecture",
    assessmentCounts: { draft: 0, scheduled: 1, live: 0, evaluating: 3, resultsPublished: 0 }
  },
  {
    id: "c6",
    code: "NURS-110",
    title: "Nursing History and Theory",
    academicYear: "2025-2026",
    term: "Fall 2025",
    cohort: "BSN Cohort Bravo",
    startDate: "2025-09-01",
    endDate: "2025-12-15",
    credits: 3,
    instructor: "Dr. Ruth Benner",
    enrollmentCount: 60,
    status: "Archived",
    courseType: "Seminar",
    assessmentCounts: { draft: 0, scheduled: 0, live: 0, evaluating: 6, resultsPublished: 0 }
  },
  {
    id: "c7",
    code: "NURS-211",
    title: "Clinical Sciences II",
    academicYear: "2025-2026",
    term: "Summer 2026",
    cohort: "BSN Cohort Charlie",
    startDate: "2026-06-01",
    endDate: "2026-08-31",
    credits: 4,
    instructor: "Dr. Nadia Rahman",
    enrollmentCount: 40,
    status: "Active",
    courseType: "Lecture",
    assessmentCounts: { draft: 0, scheduled: 2, live: 1, evaluating: 3, resultsPublished: 0 }
  },
  {
    id: "c8",
    code: "NURS-340",
    title: "Pathophysiology",
    academicYear: "2025-2026",
    term: "Summer 2026",
    cohort: "BSN Cohort Charlie",
    startDate: "2026-06-01",
    endDate: "2026-08-31",
    credits: 4,
    instructor: "Dr. Marcus Chen",
    enrollmentCount: 22,
    status: "Active",
    courseType: "Lecture",
    assessmentCounts: { draft: 0, scheduled: 1, live: 1, evaluating: 3, resultsPublished: 0 }
  },
  {
    id: "c9",
    code: "NURS-420",
    title: "Nursing Research",
    academicYear: "2026-2027",
    term: "Fall 2026",
    cohort: "BSN Cohort Alpha",
    startDate: "2026-09-01",
    endDate: "2026-12-15",
    credits: 4,
    instructor: "Dr. Ivy Santos",
    enrollmentCount: 55,
    status: "Draft",
    courseType: "Lecture",
    assessmentCounts: { draft: 0, scheduled: 3, live: 0, evaluating: 0, resultsPublished: 0 }
  },
  {
    id: "c10",
    code: "NURS-120",
    title: "Fundamentals of Nursing",
    academicYear: "2025-2026",
    term: "Summer 2026",
    cohort: "BSN Cohort Bravo",
    startDate: "2026-06-01",
    endDate: "2026-08-31",
    credits: 4,
    instructor: "Dr. Grace Okada",
    enrollmentCount: 48,
    status: "Active",
    courseType: "Lecture",
    assessmentCounts: { draft: 0, scheduled: 2, live: 1, evaluating: 4, resultsPublished: 0 }
  },
  {
    id: "c11",
    code: "NURS-350",
    title: "Pediatric Nursing",
    academicYear: "2025-2026",
    term: "Summer 2026",
    cohort: "BSN Cohort Charlie",
    startDate: "2026-06-01",
    endDate: "2026-08-31",
    credits: 3,
    instructor: "Dr. Leah Whitfield",
    enrollmentCount: 15,
    status: "Active",
    courseType: "Lecture",
    assessmentCounts: { draft: 0, scheduled: 1, live: 0, evaluating: 2, resultsPublished: 0 }
  },
  {
    id: "c12",
    code: "NURS-490",
    title: "Nursing Capstone",
    academicYear: "2025-2026",
    term: "Summer 2026",
    cohort: "BSN Seniors",
    startDate: "2026-06-01",
    endDate: "2026-08-31",
    credits: 4,
    instructor: "Dr. Samuel Ortiz",
    enrollmentCount: 12,
    status: "Active",
    courseType: "Lecture",
    assessmentCounts: { draft: 0, scheduled: 0, live: 1, evaluating: 2, resultsPublished: 0 }
  },
  {
    id: "c13",
    code: "NURS-360",
    title: "Behavioral Health Nursing",
    academicYear: "2025-2026",
    term: "Summer 2026",
    cohort: "BSN Cohort Alpha",
    startDate: "2026-06-01",
    endDate: "2026-08-31",
    credits: 3,
    instructor: "Dr. Nina Patel",
    enrollmentCount: 65,
    status: "Active",
    courseType: "Lecture",
    assessmentCounts: { draft: 0, scheduled: 2, live: 0, evaluating: 7, resultsPublished: 0 }
  },
  {
    id: "c14",
    code: "NURS-240",
    title: "Clinical Chemistry for Nursing",
    academicYear: "2026-2027",
    term: "Fall 2026",
    cohort: "BSN Cohort Bravo",
    startDate: "2026-09-01",
    endDate: "2026-12-15",
    credits: 4,
    instructor: "Dr. Helen Cho",
    enrollmentCount: 20,
    status: "Draft",
    courseType: "Lecture",
    assessmentCounts: { draft: 0, scheduled: 3, live: 0, evaluating: 0, resultsPublished: 0 }
  },
  {
    id: "c15",
    code: "NURS-250",
    title: "Professional Writing for Nurses",
    academicYear: "2025-2026",
    term: "Spring 2026",
    cohort: "BSN Cohort Charlie",
    startDate: "2026-01-15",
    endDate: "2026-05-15",
    credits: 3,
    instructor: "Dr. Maya Iverson",
    enrollmentCount: 25,
    status: "Archived",
    courseType: "Seminar",
    assessmentCounts: { draft: 0, scheduled: 0, live: 0, evaluating: 4, resultsPublished: 0 }
  },
  {
    id: "c16",
    code: "NURS-430",
    title: "Health Policy",
    academicYear: "2025-2026",
    term: "Summer 2026",
    cohort: "BSN Cohort Alpha",
    startDate: "2026-06-01",
    endDate: "2026-08-31",
    credits: 3,
    instructor: "Dr. Robert Lang",
    enrollmentCount: 80,
    status: "Active",
    courseType: "Lecture",
    assessmentCounts: { draft: 0, scheduled: 1, live: 1, evaluating: 2, resultsPublished: 0 }
  },
  {
    id: "c17",
    code: "NURS-440",
    title: "Health Care Systems",
    academicYear: "2025-2026",
    term: "Fall 2025",
    cohort: "BSN Cohort Bravo",
    startDate: "2025-09-01",
    endDate: "2025-12-15",
    credits: 3,
    instructor: "Dr. Aisha Mensah",
    enrollmentCount: 45,
    status: "Archived",
    courseType: "Seminar",
    assessmentCounts: { draft: 0, scheduled: 0, live: 0, evaluating: 3, resultsPublished: 0 }
  },
  {
    id: "c18",
    code: "NURS-450",
    title: "Population Health",
    academicYear: "2025-2026",
    term: "Summer 2026",
    cohort: "BSN Cohort Charlie",
    startDate: "2026-06-01",
    endDate: "2026-08-31",
    credits: 3,
    instructor: "Dr. Tomas Alvarez",
    enrollmentCount: 70,
    status: "Active",
    courseType: "Lecture",
    assessmentCounts: { draft: 0, scheduled: 2, live: 0, evaluating: 2, resultsPublished: 0 }
  },
  {
    id: "c19",
    code: "NURS-260",
    title: "Nursing Ethics",
    academicYear: "2026-2027",
    term: "Fall 2026",
    cohort: "BSN Cohort Alpha",
    startDate: "2026-09-01",
    endDate: "2026-12-15",
    credits: 3,
    instructor: "Dr. Claire Nguyen",
    enrollmentCount: 35,
    status: "Draft",
    courseType: "Lecture",
    assessmentCounts: { draft: 0, scheduled: 1, live: 0, evaluating: 0, resultsPublished: 0 }
  },
  {
    id: "c20",
    code: "NURS-460",
    title: "Environmental Health",
    academicYear: "2025-2026",
    term: "Spring 2026",
    cohort: "BSN Cohort Bravo",
    startDate: "2026-01-15",
    endDate: "2026-05-15",
    credits: 4,
    instructor: "Dr. Farah Siddiqui",
    enrollmentCount: 28,
    status: "Active",
    courseType: "Lecture",
    assessmentCounts: { draft: 0, scheduled: 0, live: 0, evaluating: 5, resultsPublished: 0 }
  },
  {
    id: "c21",
    code: "NURS-130",
    title: "Therapeutic Communication",
    academicYear: "2025-2026",
    term: "Summer 2026",
    cohort: "BSN Cohort Charlie",
    startDate: "2026-06-01",
    endDate: "2026-08-31",
    credits: 2,
    instructor: "Dr. Owen Blake",
    enrollmentCount: 90,
    status: "Active",
    courseType: "Lecture",
    assessmentCounts: { draft: 0, scheduled: 1, live: 1, evaluating: 3, resultsPublished: 0 }
  },
  {
    id: "c22",
    code: "NURS-470",
    title: "Informatics in Nursing",
    academicYear: "2025-2026",
    term: "Summer 2026",
    cohort: "BSN Cohort Alpha",
    startDate: "2026-06-01",
    endDate: "2026-08-31",
    credits: 4,
    instructor: "Dr. Kim Park",
    enrollmentCount: 38,
    status: "Active",
    courseType: "Lecture",
    assessmentCounts: { draft: 0, scheduled: 3, live: 0, evaluating: 2, resultsPublished: 0 }
  },
  {
    id: "c23",
    code: "NURS-480",
    title: "Immunology for Nursing",
    academicYear: "2026-2027",
    term: "Fall 2026",
    cohort: "BSN Seniors",
    startDate: "2026-09-01",
    endDate: "2026-12-15",
    credits: 4,
    instructor: "Dr. Rosa Delgado",
    enrollmentCount: 15,
    status: "Draft",
    courseType: "Lecture",
    assessmentCounts: { draft: 0, scheduled: 2, live: 0, evaluating: 0, resultsPublished: 0 }
  },
  {
    id: "c24",
    code: "NURS-425",
    title: "Evidence-Based Practice",
    academicYear: "2025-2026",
    term: "Spring 2026",
    cohort: "BSN Seniors",
    startDate: "2026-01-15",
    endDate: "2026-05-15",
    credits: 3,
    instructor: "Dr. Peter Walsh",
    enrollmentCount: 10,
    status: "Archived",
    courseType: "Seminar",
    assessmentCounts: { draft: 0, scheduled: 0, live: 0, evaluating: 4, resultsPublished: 0 }
  },
  {
    id: "c25",
    code: "NURS-370",
    title: "Mental Health Nursing",
    academicYear: "2025-2026",
    term: "Summer 2026",
    cohort: "BSN Cohort Alpha",
    startDate: "2026-06-01",
    endDate: "2026-08-31",
    credits: 3,
    instructor: "Dr. Hannah Kim",
    enrollmentCount: 30,
    status: "Active",
    courseType: "Lecture",
    assessmentCounts: { draft: 0, scheduled: 1, live: 0, evaluating: 3, resultsPublished: 0 }
  }
]

export const MOCK_ASSESSMENTS: Assessment[] = [
  { id: "a1", code: "ASM-101", title: "Clinical Sciences I Midterm", course: "NURS-210", dueDate: "2026-10-15", questionCount: 25, status: "Evaluating" },
  { id: "a2", code: "ASM-102", title: "Pharmacology Quiz", course: "NURS-320", dueDate: "2026-09-22", questionCount: 15, status: "Evaluating" },
  { id: "a3", code: "ASM-103", title: "Health Assessment Skills Check", course: "NURS-230", dueDate: "2026-10-01", questionCount: 10, status: "Scheduled" },
  { id: "a4", code: "ASM-104", title: "Clinical Sciences II Practical", course: "NURS-211", dueDate: "2026-12-10", questionCount: 50, status: "Draft" },
  { id: "a5", code: "ASM-105", title: "Pathophysiology Exam", course: "NURS-340", dueDate: "2026-11-05", questionCount: 20, status: "Scheduled" },
  { id: "a6", code: "ASM-106", title: "Adult Health Nursing Quiz", course: "NURS-330", dueDate: "2026-10-20", questionCount: 12, status: "Live" },
  { id: "a7", code: "ASM-107", title: "Community Health Case Essay", course: "NURS-410", dueDate: "2027-02-14", questionCount: 5, status: "Draft" },
  { id: "a8", code: "ASM-108", title: "Pediatric Nursing Case Analysis", course: "NURS-350", dueDate: "2026-10-30", questionCount: 8, status: "Scheduled" },
  { id: "a9", code: "ASM-109", title: "Clinical Sciences I Final Preview", course: "NURS-210", dueDate: "2026-12-15", questionCount: 15, status: "Draft" },
  { id: "a10", code: "ASM-110", title: "Pharmacology Midterm", course: "NURS-320", dueDate: "2026-10-10", questionCount: 30, status: "Evaluating" },
  { id: "a11", code: "ASM-111", title: "Nursing Research Quiz", course: "NURS-420", dueDate: "2027-03-01", questionCount: 15, status: "Draft" },
  { id: "a12", code: "ASM-112", title: "Fundamentals of Nursing Test", course: "NURS-120", dueDate: "2026-09-30", questionCount: 20, status: "Results Published" }
]

hydrateCreatedAssessments(MOCK_ASSESSMENTS)

/** Resolve lifecycle status from hub id, code, or course-offering slug (`CS101-a2`). */
export function resolveAssessmentLifecycleStatus(
  assessmentId: string | undefined,
): Assessment["status"] | undefined {
  if (!assessmentId) return undefined
  const offeringMatch = assessmentId.match(/-a(\d+)$/)
  if (offeringMatch) {
    const offeringSlug = `a${offeringMatch[1]}`
    const offeringStatus = offeringAssessmentStatusBySlug()
    if (offeringSlug in offeringStatus) {
      return offeringStatus[offeringSlug]
    }
  }
  const hub = MOCK_ASSESSMENTS.find(
    (row) => row.id === assessmentId || row.code === assessmentId,
  )
  return hub?.status
}

const DUMMY_ASSESSMENT_NAMES = [
  "Unit Exam",
  "Medication Quiz",
  "Skills Check",
  "Cumulative Final",
  "Case Study Draft",
  "Make-up Exam",
  "Extra Credit Quiz",
  "Final Preview",
  "Capstone Proposal",
]

export function resolveAssessmentTitle(assessmentId?: string): string {
  return resolveAssessmentContext(assessmentId).title
}

export type ResolvedAssessmentContext = {
  id: string
  title: string
  courseCode: string
}

/** Same lookup chain as assessment-details (mock record → composite id → fallback). */
export function resolveAssessmentContext(
  assessmentId?: string,
): ResolvedAssessmentContext {
  const found = MOCK_ASSESSMENTS.find(
    (a) => a.id === assessmentId || a.code === assessmentId,
  )
  if (found) {
    return { id: found.id, title: found.title, courseCode: found.course }
  }

  if (assessmentId && assessmentId.includes("-a")) {
    const offering = findCourseOfferingAssessment(assessmentId)
    if (offering) {
      return {
        id: offering.id,
        title: offering.name,
        courseCode: assessmentId.split("-a")[0]!,
      }
    }
    const parts = assessmentId.split("-a")
    const courseCode = parts[0]
    const idx = parseInt(parts[1], 10) - 1
    const name = DUMMY_ASSESSMENT_NAMES[idx] || DUMMY_ASSESSMENT_NAMES[0]
    return {
      id: assessmentId,
      title: `${courseCode} ${name}`,
      courseCode,
    }
  }

  const fallback = MOCK_ASSESSMENTS[0]
  return {
    id: fallback?.id ?? "a1",
    title: fallback?.title ?? "Assessment",
    courseCode: fallback?.course ?? "NURS-210",
  }
}

export function resolveCourseForAssessment(
  courseCode: string,
): CourseOffering | null {
  const normalized = courseCode.toLowerCase()
  return (
    MOCK_COURSES.find(
      (c) =>
        c.code.toLowerCase() === normalized ||
        c.id.toLowerCase() === normalized,
    ) ?? null
  )
}

export type ExamManagementBreadcrumb = {
  label: string
  href?: string
}

/** Course-offering trail shared by assessment and submission detail pages. */
export function buildCourseAssessmentBreadcrumbs(
  assessmentId: string,
  options?: { linkAssessment?: boolean },
): ExamManagementBreadcrumb[] {
  const assessment = resolveAssessmentContext(assessmentId)
  const course = resolveCourseForAssessment(assessment.courseCode)
  const items: ExamManagementBreadcrumb[] = [
    { label: "Exam Management" },
    { label: "Course Offerings", href: "/exam-management/course-offerings" },
  ]

  if (course) {
    items.push({
      label: `${course.code} · ${course.title}`,
      href: `/exam-management/course-offerings/${course.id}`,
    })
  }

  if (options?.linkAssessment) {
    items.push({
      label: assessment.title,
      href: course
        ? `/exam-management/course-offerings/${course.id}/assessments/${assessment.id}`
        : `/exam-management/assessments/${assessment.id}`,
    })
  }

  return items
}

export function adjustSubmissionByAssessmentStatus(submission: Submission, assessmentStatus?: string): Submission {
  const assessment = MOCK_ASSESSMENTS.find(
    a => a.title.toLowerCase() === submission.assessmentTitle.toLowerCase()
  );
  
  const status = assessmentStatus || assessment?.status;
  if (!status) return submission;

  const normStatus = status.toLowerCase();
  const updated = { ...submission };

  if (normStatus === "results published") {
    updated.status = "Graded";
    if (updated.grade === "Pending" || !updated.grade) {
      updated.grade = "B";
      updated.score = 85;
    }
    if (!updated.submittedAt) {
      const dateVal = assessment?.dueDate || "2026-06-18";
      updated.submittedAt = `${dateVal} 14:30`;
    }
  } else if (normStatus === "evaluating") {
    if (!updated.submittedAt) {
      const dateVal = assessment?.dueDate || "2026-06-18";
      updated.submittedAt = `${dateVal} 14:30`;
    }
    if (updated.status === "Graded") {
      if (updated.grade === "Pending" || !updated.grade) {
        updated.grade = "B";
        updated.score = 85;
      }
    } else {
      updated.status = "In-grading";
      updated.grade = "Pending";
      updated.score = 0;
    }
  } else if (normStatus === "scheduled" || normStatus === "draft" || normStatus === "live") {
    updated.submittedAt = "";
    updated.status = "";
    updated.grade = "";
    updated.score = 0;
  }

  return updated;
}

const STATIC_MOCK_SUBMISSIONS: Submission[] = [
  { id: "s1", studentId: "STU-001", studentName: "John Doe", assessmentTitle: "Clinical Sciences I Midterm", submittedAt: "2026-10-14 14:32", grade: "A", score: 92, status: "Graded" },
  { id: "s2", studentId: "STU-002", studentName: "Jane Smith", assessmentTitle: "Clinical Sciences I Midterm", submittedAt: "2026-10-15 08:11", grade: "B", score: 85, status: "Graded" },
  { id: "s3", studentId: "STU-003", studentName: "Alice Johnson", assessmentTitle: "Pharmacology Midterm", submittedAt: "2026-10-09 23:45", grade: "Pending", score: 0, status: "In-grading" },
  { id: "s4", studentId: "STU-004", studentName: "Bob Brown", assessmentTitle: "Clinical Sciences I Midterm", submittedAt: "2026-10-15 11:59", grade: "C", score: 74, status: "Graded" },
  { id: "s5", studentId: "STU-001", studentName: "John Doe", assessmentTitle: "Pharmacology Midterm", submittedAt: "2026-10-10 09:15", grade: "Pending", score: 0, status: "In-grading" },
  { id: "s6", studentId: "STU-002", studentName: "Jane Smith", assessmentTitle: "Health Assessment Skills Check", submittedAt: "2026-09-30 18:22", grade: "A+", score: 98, status: "Graded" },
  { id: "s7", studentId: "STU-005", studentName: "Charlie Green", assessmentTitle: "Adult Health Nursing Quiz", submittedAt: "2026-10-19 16:04", grade: "A-", score: 90, status: "Graded" },
  { id: "s8", studentId: "STU-006", studentName: "Diana Prince", assessmentTitle: "Pediatric Nursing Case Analysis", submittedAt: "2026-10-29 12:00", grade: "Pending", score: 0, status: "In-grading" },
  { id: "s9", studentId: "STU-003", studentName: "Alice Johnson", assessmentTitle: "Health Assessment Skills Check", submittedAt: "2026-10-01 01:10", grade: "B-", score: 81, status: "Graded" },
  { id: "s10", studentId: "STU-007", studentName: "Evan Wright", assessmentTitle: "Clinical Sciences I Midterm", submittedAt: "2026-10-15 11:30", grade: "F", score: 55, status: "Graded" },
  { id: "s11", studentId: "STU-008", studentName: "Fiona Gallagher", assessmentTitle: "Fundamentals of Nursing Test", submittedAt: "2026-09-29 10:20", grade: "B+", score: 88, status: "Graded" },
  { id: "s12", studentId: "STU-009", studentName: "George Bluth", assessmentTitle: "Pharmacology Quiz", submittedAt: "2026-09-21 15:45", grade: "C+", score: 78, status: "Graded" },
  { id: "s13", studentId: "STU-001", studentName: "John Doe", assessmentTitle: "Fundamentals of Nursing Test", submittedAt: "2026-09-29 11:05", grade: "B+", score: 88, status: "Graded" },
]

export const MOCK_SUBMISSIONS: Submission[] = STATIC_MOCK_SUBMISSIONS.map(s => adjustSubmissionByAssessmentStatus(s));

export const MOCK_STUDENTS: StudentRecord[] = [
  { id: "st1", studentId: "STU-001", name: "John Doe", email: "john.doe@riverbend.edu", phone: "555-0101", gpa: 3.65, courses: ["NURS-210", "NURS-320", "NURS-211"], assessments: ["ASM-101", "ASM-110", "ASM-109"], status: "Active", group: "Group A", enrollment: "Fall 2024", graduation: "Spring 2028", campus: "Main Campus", cohort: "BSN Cohort Alpha", studentCategory: "Undergraduate" },
  { id: "st2", studentId: "STU-002", name: "Jane Smith", email: "jane.smith@riverbend.edu", phone: "555-0102", gpa: 3.89, courses: ["NURS-210", "NURS-320", "NURS-230"], assessments: ["ASM-101", "ASM-102", "ASM-103"], status: "Active", group: "Group B", enrollment: "Fall 2024", graduation: "Spring 2028", campus: "Main Campus", cohort: "BSN Cohort Bravo", studentCategory: "Undergraduate" },
  { id: "st3", studentId: "STU-003", name: "Alice Johnson", email: "alice.j@riverbend.edu", phone: "555-0103", gpa: 3.42, courses: ["NURS-320", "NURS-230", "NURS-120"], assessments: ["ASM-102", "ASM-103", "ASM-112"], status: "Active", group: "Group A", enrollment: "Spring 2025", graduation: "Fall 2028", campus: "North Campus", cohort: "BSN Cohort Bravo", studentCategory: "Undergraduate" },
  { id: "st4", studentId: "STU-004", name: "Bob Brown", email: "bob.brown@riverbend.edu", phone: "555-0104", gpa: 2.98, courses: ["NURS-210", "NURS-211", "NURS-120"], assessments: ["ASM-101", "ASM-104", "ASM-112"], status: "Active", group: "Group C", enrollment: "Fall 2023", graduation: "Spring 2027", campus: "Main Campus", cohort: "BSN Cohort Alpha", studentCategory: "Undergraduate" },
  { id: "st5", studentId: "STU-005", name: "Charlie Green", email: "c.green@riverbend.edu", phone: "555-0105", gpa: 3.55, courses: ["NURS-330", "NURS-211", "NURS-230"], assessments: ["ASM-106", "ASM-104", "ASM-103"], status: "Active", group: "Group B", enrollment: "Fall 2025", graduation: "Spring 2029", campus: "East Campus", cohort: "BSN Cohort Alpha", studentCategory: "Undergraduate" },
  { id: "st6", studentId: "STU-006", name: "Diana Prince", email: "diana.p@riverbend.edu", phone: "555-0106", gpa: 4.00, courses: ["NURS-350", "NURS-410"], assessments: ["ASM-108", "ASM-107"], status: "Active", group: "Group A", enrollment: "Fall 2023", graduation: "Spring 2027", campus: "Main Campus", cohort: "BSN Cohort Charlie", studentCategory: "Undergraduate" },
  { id: "st7", studentId: "STU-007", name: "Evan Wright", email: "evan.w@riverbend.edu", phone: "555-0107", gpa: 2.12, courses: ["NURS-210", "NURS-420"], assessments: ["ASM-101", "ASM-111"], status: "Active", group: "Group D", enrollment: "Fall 2024", graduation: "Spring 2028", campus: "North Campus", cohort: "BSN Cohort Alpha", studentCategory: "Undergraduate" },
  { id: "st8", studentId: "STU-008", name: "Fiona Gallagher", email: "fiona.g@riverbend.edu", phone: "555-0108", gpa: 3.01, courses: ["NURS-120", "NURS-410"], assessments: ["ASM-112", "ASM-107"], status: "Active", group: "Group B", enrollment: "Spring 2024", graduation: "Fall 2027", campus: "Main Campus", cohort: "BSN Cohort Bravo", studentCategory: "Undergraduate" },
  { id: "st9", studentId: "STU-009", name: "George Bluth", email: "gbluth@riverbend.edu", phone: "555-0109", gpa: 2.75, courses: ["NURS-320", "NURS-350"], assessments: ["ASM-102", "ASM-108"], status: "Inactive", group: "Group C", enrollment: "Fall 2022", graduation: "Spring 2026", campus: "East Campus", cohort: "BSN Cohort Bravo", studentCategory: "Undergraduate" },
  { id: "st10", studentId: "STU-010", name: "Hannah Abbott", email: "habbott@riverbend.edu", phone: "555-0110", gpa: 3.33, courses: ["NURS-211", "NURS-230"], assessments: ["ASM-104", "ASM-103"], status: "Active", group: "Group A", enrollment: "Fall 2024", graduation: "Spring 2028", campus: "Main Campus", cohort: "BSN Cohort Charlie", studentCategory: "Undergraduate" },
  { id: "st11", studentId: "STU-011", name: "Ian Malcolm", email: "imalcolm@riverbend.edu", phone: "555-0111", gpa: 3.91, courses: ["NURS-330", "NURS-230"], assessments: ["ASM-106", "ASM-103"], status: "Active", group: "Group D", enrollment: "Fall 2023", graduation: "Spring 2027", campus: "North Campus", cohort: "BSN Cohort Alpha", studentCategory: "Undergraduate" },
  { id: "st12", studentId: "STU-012", name: "Julia Roberts", email: "jroberts@riverbend.edu", phone: "555-0112", gpa: 3.47, courses: ["NURS-410", "NURS-350"], assessments: ["ASM-107", "ASM-108"], status: "Active", group: "Group B", enrollment: "Spring 2025", graduation: "Fall 2028", campus: "Main Campus", cohort: "BSN Cohort Charlie", studentCategory: "Undergraduate" },
  { id: "st13", studentId: "STU-013", name: "Maya Patel", email: "maya.patel@riverbend.edu", phone: "555-0113", gpa: 3.20, courses: ["NURS-110", "NURS-340", "NURS-490", "NURS-440", "NURS-460", "NURS-470"], assessments: [], status: "Active", group: "Group A", enrollment: "Fall 2024", graduation: "Spring 2028", campus: "Main Campus", cohort: "BSN Cohort Alpha", studentCategory: "Undergraduate" },
  { id: "st14", studentId: "STU-014", name: "Noah Ramirez", email: "noah.ramirez@riverbend.edu", phone: "555-0114", gpa: 3.95, courses: ["NURS-110", "NURS-340", "NURS-360", "NURS-450", "NURS-130", "NURS-480"], assessments: [], status: "Active", group: "Group B", enrollment: "Fall 2024", graduation: "Spring 2028", campus: "Main Campus", cohort: "BSN Cohort Bravo", studentCategory: "Undergraduate" },
  { id: "st15", studentId: "STU-015", name: "Priya Shah", email: "priya.shah@riverbend.edu", phone: "555-0115", gpa: 3.75, courses: ["NURS-110", "NURS-490", "NURS-240", "NURS-260", "NURS-470", "NURS-425"], assessments: [], status: "Active", group: "Group C", enrollment: "Spring 2025", graduation: "Fall 2028", campus: "North Campus", cohort: "BSN Cohort Charlie", studentCategory: "Undergraduate" },
  { id: "st16", studentId: "STU-016", name: "Liam Okafor", email: "liam.okafor@riverbend.edu", phone: "555-0116", gpa: 3.82, courses: ["NURS-340", "NURS-360", "NURS-250", "NURS-260", "NURS-480", "NURS-370"], assessments: [], status: "Active", group: "Group D", enrollment: "Fall 2023", graduation: "Spring 2027", campus: "Main Campus", cohort: "BSN Cohort Alpha", studentCategory: "Undergraduate" },
  { id: "st17", studentId: "STU-017", name: "Elena Rossi", email: "elena.rossi@riverbend.edu", phone: "555-0117", gpa: 4.00, courses: ["NURS-490", "NURS-240", "NURS-430", "NURS-460", "NURS-470", "NURS-425"], assessments: [], status: "Active", group: "Group A", enrollment: "Fall 2023", graduation: "Spring 2027", campus: "Main Campus", cohort: "BSN Cohort Bravo", studentCategory: "Undergraduate" },
  { id: "st18", studentId: "STU-018", name: "Jordan Blake", email: "jordan.blake@riverbend.edu", phone: "555-0118", gpa: 3.90, courses: ["NURS-360", "NURS-250", "NURS-440", "NURS-460", "NURS-480", "NURS-370"], assessments: [], status: "Active", group: "Group B", enrollment: "Spring 2024", graduation: "Fall 2027", campus: "Main Campus", cohort: "BSN Cohort Charlie", studentCategory: "Undergraduate" },
  { id: "st19", studentId: "STU-019", name: "Harper Quinn", email: "harper.quinn@riverbend.edu", phone: "555-0119", gpa: 3.88, courses: ["NURS-240", "NURS-430", "NURS-440", "NURS-450", "NURS-130", "NURS-425"], assessments: [], status: "Active", group: "Group C", enrollment: "Fall 2024", graduation: "Spring 2028", campus: "East Campus", cohort: "BSN Cohort Alpha", studentCategory: "Undergraduate" },
  { id: "st20", studentId: "STU-020", name: "Samir Khan", email: "samir.khan@riverbend.edu", phone: "555-0120", gpa: 3.60, courses: ["NURS-250", "NURS-430", "NURS-450", "NURS-260", "NURS-130", "NURS-370"], assessments: [], status: "Active", group: "Group D", enrollment: "Fall 2024", graduation: "Spring 2028", campus: "West Campus", cohort: "BSN Cohort Bravo", studentCategory: "Undergraduate" }
]

export const MOCK_FACULTY: FacultyRecord[] = [
  { id: "f1", facultyId: "FAC-001", name: "Dr. Alan Turing", email: "a.turing@riverbend.edu", phone: "555-0201", status: "Active", role: "Instructor", facultyType: "Faculty", adminPosition: "Department Chair", rank: "Professor", lastUpdated: "2026-06-15", department: "Adult Health Nursing", campus: "Main Campus", category: "Full-time", courses: ["NURS-210", "NURS-211"], assessmentsCount: 5 },
  { id: "f2", facultyId: "FAC-002", name: "Dr. Rosalind Franklin", email: "r.franklin@riverbend.edu", phone: "555-0202", status: "Active", role: "Instructor", facultyType: "Faculty", adminPosition: "None", rank: "Associate Professor", lastUpdated: "2026-06-12", department: "Pharmacology", campus: "Main Campus", category: "Full-time", courses: ["NURS-320", "NURS-340"], assessmentsCount: 6 },
  { id: "f3", facultyId: "FAC-003", name: "Dr. Katherine Johnson", email: "k.johnson@riverbend.edu", phone: "555-0203", status: "Active", role: "Instructor", facultyType: "Faculty", adminPosition: "None", rank: "Professor", lastUpdated: "2026-06-14", department: "Health Assessment", campus: "Main Campus", category: "Full-time", courses: ["NURS-230", "NURS-420"], assessmentsCount: 5 },
  { id: "f4", facultyId: "FAC-004", name: "Dr. Richard Feynman", email: "r.feynman@riverbend.edu", phone: "555-0204", status: "Active", role: "Instructor", facultyType: "Faculty", adminPosition: "Director of Skills Lab", rank: "Professor", lastUpdated: "2026-06-11", department: "Fundamentals of Nursing", campus: "Main Campus", category: "Full-time", courses: ["NURS-330", "NURS-120"], assessmentsCount: 4 },
  { id: "f5", facultyId: "FAC-005", name: "Dr. Maya Angelou", email: "m.angelou@riverbend.edu", phone: "555-0205", status: "Active", role: "Instructor", facultyType: "Faculty", adminPosition: "None", rank: "Professor", lastUpdated: "2026-06-10", department: "Community Health Nursing", campus: "Main Campus", category: "Full-time", courses: ["NURS-410"], assessmentsCount: 2 },
  { id: "f6", facultyId: "FAC-006", name: "Dr. Gregor Mendel", email: "g.mendel@riverbend.edu", phone: "555-0206", status: "Active", role: "Co-Instructor", facultyType: "Faculty", adminPosition: "None", rank: "Associate Professor", lastUpdated: "2026-06-09", department: "Pharmacology", campus: "Main Campus", category: "Adjunct", courses: ["NURS-340"], assessmentsCount: 3 },
  { id: "f7", facultyId: "FAC-007", name: "Dr. Grace Hopper", email: "g.hopper@riverbend.edu", phone: "555-0207", status: "Active", role: "Co-Instructor", facultyType: "Faculty", adminPosition: "None", rank: "Professor", lastUpdated: "2026-06-08", department: "Adult Health Nursing", campus: "Main Campus", category: "Full-time", courses: ["NURS-211", "NURS-210"], assessmentsCount: 4 },
  { id: "f8", facultyId: "FAC-008", name: "Dr. Isaac Newton", email: "i.newton@riverbend.edu", phone: "555-0208", status: "Active", role: "Instructor", facultyType: "Faculty", adminPosition: "None", rank: "Professor", lastUpdated: "2026-06-07", department: "Health Assessment", campus: "Main Campus", category: "Full-time", courses: ["NURS-420"], assessmentsCount: 2 },
  { id: "f9", facultyId: "FAC-009", name: "Dr. Galileo Galilei", email: "g.galilei@riverbend.edu", phone: "555-0209", status: "Active", role: "Instructor", facultyType: "Faculty", adminPosition: "None", rank: "Professor", lastUpdated: "2026-06-06", department: "Fundamentals of Nursing", campus: "Main Campus", category: "Full-time", courses: ["NURS-120"], assessmentsCount: 3 },
  { id: "f10", facultyId: "FAC-010", name: "Dr. Leonardo da Vinci", email: "l.vinci@riverbend.edu", phone: "555-0210", status: "Active", role: "Instructor", facultyType: "Faculty", adminPosition: "None", rank: "Professor", lastUpdated: "2026-06-05", department: "Pediatric Nursing", campus: "Main Campus", category: "Full-time", courses: ["NURS-350"], assessmentsCount: 2 },
  { id: "f11", facultyId: "FAC-011", name: "Dr. Ada Lovelace", email: "a.lovelace@riverbend.edu", phone: "555-0211", status: "Active", role: "Instructor", facultyType: "Faculty", adminPosition: "None", rank: "Professor", lastUpdated: "2026-06-04", department: "Adult Health Nursing", campus: "Main Campus", category: "Full-time", courses: ["NURS-490"], assessmentsCount: 2 },
  { id: "f12", facultyId: "FAC-012", name: "Dr. Albert Einstein", email: "a.einstein@riverbend.edu", phone: "555-0212", status: "Inactive", role: "Instructor", facultyType: "Faculty", adminPosition: "None", rank: "Professor", lastUpdated: "2026-05-30", department: "Fundamentals of Nursing", campus: "Main Campus", category: "Full-time", courses: [], assessmentsCount: 0 },
  { id: "f13", facultyId: "FAC-013", name: "Margaret Hamilton", email: "m.hamilton@riverbend.edu", phone: "555-0213", status: "Active", role: "Teaching Assistant", facultyType: "Staff", adminPosition: "Lead Lab Coordinator", rank: "Lecturer", lastUpdated: "2026-06-13", department: "Adult Health Nursing", campus: "Main Campus", category: "Full-time", courses: ["NURS-210", "NURS-211"], assessmentsCount: 4 }
]

/**
 * Academic years for the settings hub. Moved out of the page so the
 * shape is visible to anything else that needs to name a year.
 */
export interface AcademicTerm {
  id: string
  label: string
  startDate: string | null
  endDate: string | null
}

export interface AcademicYear {
  id: string
  label: string
  startDate: string | null
  endDate: string | null
  terms: AcademicTerm[]
}

export const MOCK_ACADEMIC_YEARS: AcademicYear[] = [
  {
    id: "ay-2025-26",
    label: "2025-26",
    startDate: null,
    endDate: null,
    terms: [
      { id: "fall-2025", label: "Fall 2025", startDate: null, endDate: null },
      { id: "spring-2026", label: "Spring 2026", startDate: null, endDate: null },
      { id: "summer-2026", label: "Summer 2026", startDate: null, endDate: null },
    ],
  },
  {
    id: "ay-2026-27",
    label: "2026-27",
    startDate: null,
    endDate: null,
    terms: [
      { id: "fall-2026", label: "Fall 2026", startDate: null, endDate: null },
      { id: "spring-2027", label: "Spring 2027", startDate: null, endDate: null },
      { id: "summer-2027", label: "Summer 2027", startDate: null, endDate: null },
    ],
  },
]
